import { Zap, Clock, TrendingUp, Sparkles, Star } from "lucide-react";
import StandardSection from "@/components/ui/StandardSection";
import { useEffect, useState } from "react";

export default function PricingCTA() {

  const [loading, setLoading] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [point1Title, setPoint1Title] = useState<string>("");
  const [point1Subtitle, setPoint1Subtitle] = useState<string>("");
  const [point2Title, setPoint2Title] = useState<string>("");
  const [point2Subtitle, setPoint2Subtitle] = useState<string>("");
  const [pricingValue, setPricingValue] = useState<string>("");
  const [pricingTitle, setPricingTitle] = useState<string>("");
  const [pricingPoints, setPricingPoints] = useState<string[]>([]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`
        );
        const data = await response.json();

        const landingPageData = data.find(
          (page: any) => page.name === "Landing Page"
        );
        const testimonialsData = landingPageData?.fields?.find((field: any) =>
          field?.key?.includes("revolutionaryPricingModelPanel")
        );

        // Fetch section title and description
        const sectionTitleField = testimonialsData?.components?.find((comp: any) => comp.key === "pricing_title");
        const sectionPrimaryHeader = testimonialsData?.components?.find((comp: any) => comp.key === "pricing_primary_header");
        const sectionSecondaryHeader = testimonialsData?.components?.find((comp: any) => comp.key === "pricing_secondary_header");
        const sectionDescriptionField = testimonialsData?.components?.find((comp: any) => comp.key === "pricing_description");

        const point1Panel = testimonialsData?.components?.find((comp: any) => comp.key === "additionalPoint1Panel");
        const point2Panel = testimonialsData?.components?.find((comp: any) => comp.key === "additionalPoint2Panel");
        if (point1Panel) {
          const point1TitleField = point1Panel.components.find((comp: any) => comp.key === "point1_title");
          const point1SubtitleField = point1Panel.components.find((comp: any) => comp.key === "point1_subtitle");
          setPoint1Title(point1TitleField?.defaultValue || "");
          setPoint1Subtitle(point1SubtitleField?.defaultValue || "");
        }
        if (point2Panel) {
          const point2TitleField = point2Panel.components.find((comp: any) => comp.key === "point2_title");
          const point2SubtitleField = point2Panel.components.find((comp: any) => comp.key === "point2_subtitle");
          setPoint2Title(point2TitleField?.defaultValue || "");
          setPoint2Subtitle(point2SubtitleField?.defaultValue || "");
        }


        setSectionTitle(sectionTitleField?.defaultValue || "");
        setSectionPrimaryHeader(sectionPrimaryHeader?.defaultValue || "");
        setSectionSecondaryHeader(sectionSecondaryHeader?.defaultValue || "");
        setSectionDescription(sectionDescriptionField?.defaultValue || "");

        // Fetch pricing model panel
        const pricingModelPanel = testimonialsData?.components?.find((comp: any) => comp.key === "pricingModelPanel");
        let pricingValueField, pricingTitleField, pricingPointsField;
        if (pricingModelPanel) {
          pricingValueField = pricingModelPanel.components?.find((comp: any) => comp.key === "pricing_value");
          pricingTitleField = pricingModelPanel.components?.find((comp: any) => comp.key === "pricing_point_title");
          pricingPointsField = pricingModelPanel.components?.find((comp: any) => comp.key === "pricing_points");
        }
        setPricingValue(pricingValueField?.defaultValue || "");
        setPricingTitle(pricingTitleField?.defaultValue || "");
        if (pricingPointsField?.defaultValue && Array.isArray(pricingPointsField.defaultValue)) {
          setPricingPoints(pricingPointsField.defaultValue.map((pt: any) => pt.point));
        } else {
          setPricingPoints([]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <StandardSection id="pricing" background="gradient">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
        {/* Left Content */}
        <div className="space-y-6 sm:space-y-8 lg:pr-8 px-4 sm:px-0">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-forest-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm shadow-sm">
            <Sparkles className="h-4 w-4 text-primary-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
            <Zap className="h-4 w-4 text-forest-custom" />
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles">
            <span className="text-gray-900 block break-words whitespace-normal lg:whitespace-nowrap">
              {sectionPrimaryHeader}
            </span>
            <span className="bg-gradient-to-r text-green-600 bg-clip-text block break-words whitespace-normal lg:whitespace-nowrap">
              {sectionSecondaryHeader}
            </span>
          </h2>

          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
            {sectionDescription}
          </p>

          {/* Feature highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {(point1Title || point1Subtitle) && (
              <div className="flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-primary-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 float-animation flex-shrink-0" />
                <div>
                  <div className="text-gray-900 font-semibold text-sm sm:text-base">{point1Title}</div>
                  <div className="text-gray-600 text-xs sm:text-sm">{point1Subtitle}</div>
                </div>
              </div>
            )}
            {(point2Title || point2Subtitle) && (
              <div className="flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-forest-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-forest-custom float-animation flex-shrink-0" />
                <div>
                  <div className="text-gray-900 font-semibold text-sm sm:text-base">{point2Title}</div>
                  <div className="text-gray-600 text-xs sm:text-sm">{point2Subtitle}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Content - Futuristic Price Card */}
        <div className="flex justify-center lg:justify-end px-4 sm:px-0">
          <div className="relative group max-w-sm sm:max-w-md w-full">
            {/* Modern background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-transparent to-forest-custom/10 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>

            {/* Main price card */}
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 border border-primary-200 shadow-2xl hover:transform hover:scale-105 transition-all duration-500 text-center">
              {/* Top accent */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-1 bg-green-600 rounded-full"></div>

              {/* Stars decoration */}
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-accent-amber fill-current mx-1" />
                ))}
              </div>

              <div className="text-green-600 sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r mb-3">
                {pricingValue && !isNaN(Number(pricingValue))
                  ? Number(pricingValue)?.toLocaleString()
                  : pricingValue}
              </div>
              <div className="text-gray-600 text-base sm:text-lg lg:text-xl font-medium mb-4 sm:mb-6">{pricingTitle}</div>

              {/* Value propositions */}
              <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {pricingPoints.map((pt, idx) => (
                  <div key={pt + idx} className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-600">
                    <div className={`w-2 h-2 rounded-full animate-pulse flex-shrink-0 ${idx % 2 === 0 ? "bg-primary-600" : "bg-forest-custom"}`}></div>
                    {pt}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </StandardSection>
  );
}
